import React from 'react';
import { useTranslation } from 'react-i18next';
import { Customer } from '@/types/customer';
import { formatCurrency } from '@/utils/number-format';

interface CustomerInfoTabProps {
  customer: Customer | null;
  loading?: boolean;
}

const CustomerInfoTab: React.FC<CustomerInfoTabProps> = ({
  customer,
  loading = false,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">{t('customers.messages.noCustomerSelected')}</div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header - Customer Name + Code */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-xl font-semibold text-gray-900">{customer.name}</h2>
        <p className="text-sm text-gray-600">{t('customers.form.code')}: {customer.code}</p>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">{t('customers.tabs.info')}</h3>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.name')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.name || '-'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.gender')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.gender || '-'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.birth')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.birthday || '-'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.phone')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.phone || '-'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.email')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.email || '-'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.facebook')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.facebookLink || '-'}</div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">{t('customers.tabs.detailed')}</h3>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.company')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.companyName || '-'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.tax')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.taxCode || '-'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.categoryGroup')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.customerGroupName || '-'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.categoryType')}
              </label>
              <div className="mt-1 text-sm text-gray-900">{customer.customerTypeName || '-'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.debit')}
              </label>
              <div className="mt-1 text-sm text-gray-900 font-medium">
                {formatCurrency(customer.debit || 0)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('customers.form.totalSell')}
              </label>
              <div className="mt-1 text-sm text-gray-900">
                {formatCurrency(customer.totalSell || 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">{t('customers.form.address')}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('customers.form.province')}
            </label>
            <div className="mt-1 text-sm text-gray-900">{customer.address?.province || '-'}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('customers.form.city')}
            </label>
            <div className="mt-1 text-sm text-gray-900">{customer.address?.city || '-'}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('customers.form.district')}
            </label>
            <div className="mt-1 text-sm text-gray-900">{customer.address?.district || '-'}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('customers.form.commune')}
            </label>
            <div className="mt-1 text-sm text-gray-900">{customer.address?.commune || '-'}</div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('customers.form.streetAddress')}
            </label>
            <div className="mt-1 text-sm text-gray-900">{customer.address?.streetAddress || '-'}</div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">{t('customers.form.note')}</h3>
        <div className="text-sm text-gray-900 whitespace-pre-wrap">
          {customer.notes || '-'}
        </div>
      </div>

      {/* System Information */}
      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3">{t('common.systemInfo')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <span className="font-medium">{t('common.createdAt')}:</span> {formatDate(customer.createdAt)}
          </div>
          <div>
            <span className="font-medium">{t('common.updatedAt')}:</span> {formatDate(customer.updatedAt)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInfoTab;
